import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, FormArray, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { DepartmentService } from '../../../modules/settings/location/services/department.service';
import { noWhitespaceValidator } from '../../custom-validator/white-space-validator';
import { externalDepartmentShortNameValidator } from '../../custom-validator/external-department-short-name-validator';
import { SharedDataService } from '../../service/shared-data/shared-data.service';
import {
  CdkDragDrop,
  CdkDragMove,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { SpinnerComponent } from '../spinner/spinner.component';
import { PromptDialogComponent } from '../prompt-dialog/prompt-dialog.component';
import { OrganizationService } from 'src/app/modules/settings/location/services/organization.service';
import { Subject, takeUntil } from 'rxjs';
import { PermissionsService } from 'src/app/services/permissions.service';
import { PERMISSIONS } from 'src/app/constants/permissions.constants';

enum manageDialogRef {
  Indicator = 'INDICATOR',
}
@Component({
  selector: 'app-manage-dialog',
  templateUrl: './manage-dialog.component.html',
  styleUrl: './manage-dialog.component.scss',
})
export class ManageDialogComponent implements OnInit {
  @Output() updateDataEvent = new EventEmitter<any>();
  @Output() deleteDataEvent = new EventEmitter<any>();
  @Output() addDataEvent = new EventEmitter<any>();
  @Output() cancelUpdateEvent = new EventEmitter<any>();
  @Output() orderingDataEvent = new EventEmitter<any>();
  @Output() editDepartmentState = new EventEmitter<any>();
  @Output() editDepartmentPlanningState = new EventEmitter<any>(); 
  @Output() editMemberTypeState = new EventEmitter<any>();
  manageDataForm!: FormGroup;
  disabledStates: boolean[] = [];
  dataList: any;
  editMode: boolean[] = [];
  deleteMode: boolean[] = [];
  isAddEnabled: boolean = false;
  dataExistsError: boolean = false;
  dataEditExistError: boolean[] = [];
  orgDepartments: any;
  dialogListData: any;
  isUpdateEnabled: boolean = false;
  isBtnDisabled: boolean = false;
  showIcon: boolean = false;
  iconList: any = [];
  isDragging: boolean[] = [];
  editSpinnerRef: any;
  manageDialogRef = manageDialogRef;
  originalValues: string[] = [];
  originalExternalDepartmentShortNameValues: string[] = [];
  enableSave: boolean = false;
  showNewForm: boolean = false;
  showToolTip: boolean = false; // for showing coverage tooltip
  organisationId =
    typeof window !== 'undefined' && window.sessionStorage
      ? sessionStorage.getItem('orgId')
      : null;
  isManagerEdit:boolean = false
  dialogListDataDup: any;
  showButtonCont:boolean = false
  dataListDup:any
  manageDataFormDup: any;
  manageDataFormExternalShortNameDup: any;
  isEditing:boolean = false // edit button clicked
  private destroy$ = new Subject<void>();
  canEditOrganisation: boolean = false;
  isAppAdmin: boolean = false;
  constructor(
    public dialogRef: MatDialogRef<ManageDialogComponent>,
    private _formBuilder: FormBuilder,
    public deptservice: DepartmentService,
    public sharedDataService: SharedDataService,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private orgService: OrganizationService,
    private permissionService: PermissionsService,  
  ) {}
  ngOnInit() {
    if (this.permissionService.getUserRole && this.permissionService.getUserRole() === 'APP_ADMIN') {
      this.isAppAdmin = true;
    }
    this.permissionService.userPermissions$
            .pipe(takeUntil(this.destroy$))
            .subscribe((permissions) => {
              this.canEditOrganisation = permissions.includes(PERMISSIONS.ORGANISATION_DETAILS_WRITE);
            });
    if (this.data?.dialogType === 'manage_dept') {
      this.fetchDepartmentData()
    } 
    else if(this.data?.type==='memberType'){
      this.getJobTypeList()
    }
    else if (this.data.formData) {
      this.dataList = this.data.formData;
      this.prepareForm();
    }
  }

  fetchDepartmentData() {
    this.deptservice.fetchOrgDepartmentsWithStatus(this.organisationId, false).subscribe({
      next: (deptData) => {
        this.dataList = deptData.body;
        this.prepareForm()
      },
      error: (error) => {
        console.error('error');
      }
    })
  }
  
  getJobTypeList(emitData?: any) {
    let urlParams = {
      activeOnly: false
    }
  this.orgService
    .getMemberTypes(this.organisationId,urlParams)
    .subscribe((memberTypeData: any) => {
      if (memberTypeData.status === 200) {
        this.dataList = memberTypeData.body;
        this.prepareForm()
      }
    });
  }

  prepareForm() {
    this.isEditing = false;
    this.enableSave = false;
    this.showButtonCont = false;
    this.isAddEnabled = false;
    this.showIcon = this.data?.type === 'icon';

    this.manageDataForm = this._formBuilder.group({
      name: this._formBuilder.array(
        this.dataList?.map(
          (dept: any) =>
            new FormControl(
              { value: dept?.name, disabled: true },
              noWhitespaceValidator()
            )
        ) || []
      ),
      externalDepartmentShortName: this._formBuilder.array(
        this.dataList?.map(
          (dept: any) =>
            new FormControl(
              { value: dept?.externalDepartmentShortName ?? '', disabled: true },
              [Validators.maxLength(2), externalDepartmentShortNameValidator()]
            )
        ) || []
      ),
      data: new FormControl(''),
      externalDepartmentShortNameData: new FormControl('', [Validators.maxLength(2), externalDepartmentShortNameValidator()]),
    });

    // Step 2️: Add dynamic controls
    if (this.data?.dialogType === 'manage_dept' || this.data?.type === 'memberType') {
      this.manageDataForm.addControl('active', new FormControl(true));
    }
    if (this.data?.dialogType === 'manage_dept' || this.data?.type === 'memberType') {
      this.manageDataForm.addControl('availableForPlanning', new FormControl(true));
    }
    

    if (this.data?.type === 'memberType') {
      // These only exist in memberType dialogs
      this.manageDataForm.addControl('isManager', new FormControl(false));
      this.manageDataForm.addControl('dutyCoveredByMemberTypes', new FormControl([]));
    }

    // Step 3️: Setup supporting data arrays
    this.dataListDup = [...this.dataList];
    this.editMode = this.dataList?.map(() => false);
    this.deleteMode = this.dataList?.map(() => true);
    this.dataEditExistError = this.dataList?.map(() => false);

    // Step 4️: Prepare dialog list data
    if (this.showIcon) {
      this.dialogListData = this.dataList?.map((dept: any) => ({
        id: dept.id,
        value: dept.name,
        icon: dept.icon,
        colourCode: dept.colourCode,
        iconColour: dept.iconColour,
        iconType: dept.iconType,
        isMappedToAnyEvent: dept.isMappedToAnyEvent,
      }));
    } else {
      this.dialogListData = this.dataList?.map((dept: any) => ({
        id: dept.id,
        value: dept.name,
        externalDepartmentShortName: dept?.externalDepartmentShortName ?? '',
        editMode: false,
        ...(this.data?.dialogType === 'manage_dept' || this.data?.type === 'memberType'
          ? { active: dept.active }
          : {}),
        ...(this.data?.dialogType === 'manage_dept')?  { availableForPlanning: dept.availableForPlanning }:{},
        ...(this.data?.dialogType === 'manage_dept'
          ? { linkageStatus: dept.linkageStatus }
          : {}),
        ...(this.data?.type === 'memberType'
          ? {
              isManager: dept.isManager,
              dutyCoveredByMemberTypes: dept.dutyCoveredByMemberTypes,
            }
          : {}),
        disabled: !this.canEditOrganisation,
      }));

      if (this.data?.dialogType === 'manage_dept') {
        this.originalValues = this.dialogListData.map((d: any) => d?.value);
        this.originalExternalDepartmentShortNameValues = this.dialogListData.map((d: any) => d?.externalDepartmentShortName || '');
      }

      this.dialogListDataDup = [...this.dialogListData];
    }

    // Step 5️: Enable update when valid
    this.manageDataForm.valueChanges.subscribe(() => {
      this.isUpdateEnabled = this.manageDataForm.valid;
    });

    // Step 6️: Maintain data refs
    if (this.data.fullData) {
      this.data.fullData = this.dataList;
    } else {
      this.data.formData = this.dataList;
    }

    // Step 7️: Duplicate name FormArray
    const originalArray = this.manageDataForm.get('name') as FormArray;
    this.manageDataFormDup = new FormArray(
      originalArray.controls.map(control => new FormControl(control.value))
    );
    const originalExternalShortNameArray = this.manageDataForm.get('externalDepartmentShortName') as FormArray;
    if (originalExternalShortNameArray) {
      this.manageDataFormExternalShortNameDup = new FormArray(
        originalExternalShortNameArray.controls.map(control => new FormControl(control.value, [Validators.maxLength(2), externalDepartmentShortNameValidator()]))
      );
    }
  }

  cancelForm(type?: string) {
    if ((this.enableSave || this.isAddEnabled || this.hasUnsavedRowEdits()) && type && type === 'reset' ) {
      this.showWarningMessageDialog();
    }  
    if (type && type === 'newForm') {
      this.manageDataForm?.get('externalDepartmentShortNameData')?.setValue('');
      this.showNewForm = false;
    } 
    else if (!(type && type === 'reset')) {
      this.dialogRef.close();
    }
  }

  hasUnsavedRowEdits(): boolean {
    if (!this.editMode || this.editMode.length === 0) return false;

    for (let i = 0; i < this.editMode.length; i++) {
      if (this.editMode[i]) {
        if (this.data?.type === 'memberType') {
          if (this.isMemberTypeControlDirty(i, this.dialogListData[i])) {
            return true;
          }
        } else if (this.data?.dialogType === 'manage_dept') {
          if (this.isControlDirty(i)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  showWarningMessageDialog() {
    const dialogRef: MatDialogRef<PromptDialogComponent> = this.dialog.open(
      PromptDialogComponent,
      {
        width: '536px',
        height: '338px',
        disableClose: true,
        data: {
          iconName: 'info',
          title: 'prompt_dialog.title',
          message: 'prompt_dialog.content',
          buttons: ['prompt_dialog.back_btn', 'prompt_dialog.ok_btn'],
          buttonName: ['Back', 'OK'],
        },
      }
    );
    dialogRef.componentInstance.buttonClicked.subscribe(
        (buttonName: string) => {
          if (buttonName === 'OK') {
            dialogRef.close();
            // Reset internal state variables
            this.dialogListData = this.dialogListDataDup;
            this.dataList = this.dataListDup;
            this.editMode = this.editMode.map(() => false);
            this.enableSave = false;
            this.isAddEnabled = false;
            this.showButtonCont = false;
            // Reset the form completely
            this.manageDataForm.reset();
            this.dialogListDataDup = [...this.dialogListData];

            // Repopulate the 'name' FormArray with backup data
            this.manageDataForm.setControl(
              'name',
              new FormArray(
                this.manageDataFormDup.controls.map((control: any) =>
                  new FormControl(control.value)
                )
              )
            );
            if (this.manageDataFormExternalShortNameDup) {
              this.manageDataForm.setControl(
                'externalDepartmentShortName',
                new FormArray(
                  this.manageDataFormExternalShortNameDup.controls.map((control: any) =>
                    new FormControl(control.value, [Validators.maxLength(2), externalDepartmentShortNameValidator()])
                  )
                )
              );
            }
            this.manageDataForm?.get('externalDepartmentShortNameData')?.setValue('');

            // Mark form as pristine and untouched
            this.manageDataForm.markAsPristine();
            this.manageDataForm.markAsUntouched();
            if(this.data?.type === 'memberType' || this.data?.dialogType === 'manage_dept'){
              this.dialogRef.close();
            }
          } else if (buttonName === 'Back') {
            dialogRef.close();
          }
        }
      );
    }

  get dialogDataControls() {
    if (this.dataList?.length > 0) {
      return this.manageDataForm?.get('name') as FormArray;
    }
    return null;
  }

  get externalDepartmentShortNameControls() {
    if (this.dataList?.length > 0) {
      return this.manageDataForm?.get('externalDepartmentShortName') as FormArray;
    }
    return null;
  }

  getExternalDepartmentShortNameControl(index: number): FormControl {
    const shortNameArray = this.externalDepartmentShortNameControls;
    return (shortNameArray?.at(index) as FormControl) || new FormControl('');
  }

  validateNewData(): void {
    const newDepartmentValue = this.manageDataForm?.get('data')?.value?.trim() || '';
    let compareDataList;
    if (this.data.fullData) {
      compareDataList = this.data.fullData;
    } else {
      compareDataList = this.data.formData;
    }
    if (newDepartmentValue.length > 0) {
      this.dataExistsError = compareDataList.some(
        (dept: { name: string }) =>
          dept.name?.toLowerCase() === newDepartmentValue.toLowerCase()
      );
      const isShortNameValid = this.data?.dialogType === 'manage_dept'
        ? Boolean(!this.manageDataForm?.get('externalDepartmentShortNameData') || this.manageDataForm.get('externalDepartmentShortNameData')?.valid)
        : true;
      this.isAddEnabled = !this.dataExistsError && isShortNameValid;
    } else {
      this.isAddEnabled = false;
    }
  }

  validateEditData(index: number, event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const newDepartmentValue = inputElement.value.trim();
    let compareDataList;
    if (this.data.fullData) {
      compareDataList = this.data.fullData;
    } else {
      compareDataList = this.data.formData;
    }
    if (newDepartmentValue.length > 0) {
      let existingDept = compareDataList.some(
        (dept: { name: string }, idx: number) =>
          idx !== index && dept.name?.toLowerCase() === newDepartmentValue.toLowerCase()
      );
      this.dataEditExistError[index] = existingDept;
    } else {
      //do nothing
    }
  }

  // manage new member type
  openNewDataForm() {
    this.showNewForm = true;
  }
  
  createNewDuty() {

  }

  addNewData() {
    this.isAddEnabled = false;
    let apiData : any = {
      orderOfListing: this.dialogListData.length+1,
      organisationId: this.organisationId,
      name: this.manageDataForm?.get('data')?.value.trim(),
    };
    if (this.data?.dialogType === 'manage_dept') {
      const shortNameVal = this.manageDataForm?.get('externalDepartmentShortNameData')?.value;
      apiData.externalDepartmentShortName = typeof shortNameVal === 'string' ? shortNameVal.trim() : (shortNameVal || '');
    }
    if ((this.data?.dialogType === 'manage_dept' || this.data?.type==='memberType') && this.manageDataForm?.contains('active')) {
      apiData.active = this.manageDataForm?.get('active')?.value;
    }
    //department planning availability
    if (this.data?.dialogType === 'manage_dept' && this.manageDataForm?.contains('availableForPlanning')) {
      apiData.availableForPlanning = this.manageDataForm?.get('availableForPlanning')?.value;
    }
    if(this.data?.type==='memberType' && this.manageDataForm.contains('isManager')){
      apiData.isManager = this.manageDataForm?.get('isManager')?.value
    }
    if(this.data?.type==='memberType' && this.manageDataForm.contains('dutyCoveredByMemberTypes')){
      apiData.dutyCoveredByMemberTypes = this.manageDataForm?.get('dutyCoveredByMemberTypes')?.value
    }
    this.addDataEvent.emit(apiData);
    this.manageDataForm?.get('data')?.setValue('');
    if (this.manageDataForm?.contains('externalDepartmentShortNameData')) {
      this.manageDataForm?.get('externalDepartmentShortNameData')?.setValue('');
    }
    if ((this.data?.dialogType === 'manage_dept' || this.data?.type==='memberType') && this.manageDataForm?.contains('active')) {
      this.manageDataForm?.get('active')?.setValue(true); // Reset toggle to checked
    }
    if (this.data?.dialogType === 'manage_dept' && this.manageDataForm?.contains('availableForPlanning')) {
      this.manageDataForm?.get('availableForPlanning')?.setValue(true);
    }
    this.isAddEnabled = false;
  }
  toggleEdit(index: number, item: any): void {
    const isEditing = (this.editMode[index] = !this.editMode[index]);
    this.isEditing = this.editMode.some(val => val);
    const dialogItem = this.dialogListData[index];
    const control = this.dialogDataControls?.at(index);
    dialogItem.editMode = isEditing;

    if (isEditing) {
      if (this.data?.dialogRef === manageDialogRef.Indicator) {
        const updatedModalData = {
          ...this.data.modalData,
          data: {
            ...this.data.modalData.data,
            content: item,
          },
        };
        this.addIconData(updatedModalData, true);
      } else {
        control?.enable();
        if (this.data?.dialogType === 'manage_dept') {
          this.externalDepartmentShortNameControls?.at(index)?.enable();
        }
        if (this.data?.type === 'memberType') {
          this.isManagerEdit = item.isManager;
        }
      }
    } else {
      if (!(this.data?.dialogRef === manageDialogRef.Indicator)) {
        control?.disable();
        if (this.data?.dialogType === 'manage_dept') {
          this.externalDepartmentShortNameControls?.at(index)?.disable();
        }
      }
    }
  }

  deleteData(index: number, deleteData: any) {
    if(this.data?.dialogRef === this.manageDialogRef.Indicator){
      this.deleteDataEvent.emit({deleteData:deleteData.id, index:index});
    }
    else{
      this.deleteSelectedData(index, deleteData)
    }
  }

  deleteSelectedData(index: number, deleteData: any){
    //for indicators, update variables only if user confirms delete
    this.editMode[index] = false;
    this.deleteMode[index] = false;
    if(this.data?.dialogRef !== this.manageDialogRef.Indicator){
      this.deleteDataEvent.emit({deleteData:deleteData.id, index:index});
    }
  }

  updateData(index: number, editDept: any) {
    let apiData:any;
    this.isUpdateEnabled = false;
    if (this.showIcon) {
      apiData = {
        organisationId: this.organisationId,
        icon: editDept.icon,
        name: this.dialogDataControls?.value?.toString(),
        colourCode: editDept.colourCode,
        editId: editDept.id,
      };
    } else {
      if (this.data?.type === 'memberType') {
        apiData = {
          organisationId: this.organisationId,
          name: this.dialogDataControls?.value?.length>1 ?this.dialogDataControls?.value[index]: this.dialogDataControls?.value?.toString(),
          editId: editDept.id,
          orderOfListing: index + 1,
          active: editDept.active,
          isManager: this.isManagerEdit,
          dutyCoveredByMemberTypes: editDept.dutyCoveredByMemberTypes.length ? editDept.dutyCoveredByMemberTypes : []
        };
      } else {
        const nameVal = this.dialogDataControls?.at(index)?.value ?? (this.dialogDataControls?.value?.length > 1 ? this.dialogDataControls?.value[index] : this.dialogDataControls?.value?.toString());
        apiData = {
          organisationId: this.organisationId,
          name: typeof nameVal === 'string' ? nameVal.trim() : nameVal,
          editId: editDept.id,
        };
        // preserve planning availability on name edits
        if (this.data?.dialogType === 'manage_dept') {
          const shortNameVal = this.externalDepartmentShortNameControls?.at(index)?.value;
          apiData.externalDepartmentShortName = typeof shortNameVal === 'string' ? shortNameVal.trim() : (shortNameVal || '');
          apiData.availableForPlanning = editDept.availableForPlanning;
        }
      }
    }
    this.isEditing = false
    this.isManagerEdit = false
    this.updateDataEvent.emit(apiData);
  }

  cancelUpdation(index: number, item: any) {
    const controls = this.dialogDataControls;
    this.isEditing = false
    if (controls) {
      controls.at(index).setValue(this.dataList[index].name);
    }
    if (this.data?.dialogType === 'manage_dept' && this.externalDepartmentShortNameControls && this.dataList[index]) {
      this.externalDepartmentShortNameControls.at(index).setValue(this.dataList[index].externalDepartmentShortName ?? '');
    }
    if (this.data?.type === 'memberType') {
      item.isManager = this.dataList[index].isManager;
      item.dutyCoveredByMemberTypes = [...(this.dataList[index].dutyCoveredByMemberTypes || [])];
      this.isManagerEdit = item.isManager;
    }
    this.toggleEdit(index, item);
    this.cancelUpdateEvent.emit();
  }

  changeManagerSel(select: boolean, item?: any) {
    this.isManagerEdit = select;
    if (item) {
      item.isManager = select;
    }
  }

  updatedDataList(dataList: any) {
    if (this.data?.dialogType === 'manage_dept'){
      this.fetchDepartmentData()
    }
    else if(this.data?.type==='memberType'){
      this.getJobTypeList()
    }
    else if(this.data?.type==='manage_duties') {
      
    }
    else if (dataList) {
      this.dataList = dataList;
      this.prepareForm();
    }
  }

  addIconData(_modalData: any, _editMode: boolean) {
    _modalData.data = {
      ..._modalData.data,
      editMode: _editMode,
    };
    let data = {
      width: _modalData.width,
      height: _modalData.height,
      disableClose: true,
      autoFocus: false,
      data: _modalData.data,
    };
    const dialogRef = this.dialog.open(_modalData.modalName, data);
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result === 'success') {
        this.addDataEvent.emit('success');
      }
    });
  }

drop(event: CdkDragDrop<any[]>) {

  const TOP_OFFSET = 163;

  const containerEl = event.container.element.nativeElement as HTMLElement;

  // Y position where item is dropped
  const pointerY = event.dropPoint.y ;

  // Get physical children (remove placeholder)
  const raw = Array.from(containerEl.children) as HTMLElement[];
  const children = raw.filter(c => !c.classList.contains('cdk-drag-placeholder'));

  // -----------------------------------------------------------
  // 1️⃣ Find which child element occupies the drop Y position
  // -----------------------------------------------------------
  let targetIdx = children.length - 1; // default to last item
  for (let i = 0; i < children.length; i++) {
    const rect = children[i].getBoundingClientRect();
    const top = rect.top;
    const bottom = rect.bottom;

    // If the pointer is inside this child's bounding box → this is the target
    if (pointerY >= top && pointerY <= bottom) {
      targetIdx = i;
      break;
    }
  }
  // -----------------------------------------------------------
  const prev = event.previousIndex;
  let newIndex = targetIdx;

  // Adjust if dragging downward
  if (newIndex >= prev) {
    // newIndex++;
  }

  // Keep inside range
  newIndex = Math.max(0, Math.min(newIndex, this.dialogListData.length - 1));

  // Apply array movement
  if (prev !== newIndex) {
    moveItemInArray(this.dialogListData, prev, newIndex);
    moveItemInArray(this.dataList, prev, newIndex);
    this.reorderFormArrayControls(prev, newIndex);
  }
}

reorderFormArrayControls(previousIndex: number, currentIndex: number) {
  const formArray = this.manageDataForm?.get('name') as FormArray;
  // Get the control at the previous index
  const controlToMove = formArray.at(previousIndex);
  // Remove the control from the previous index
  formArray.removeAt(previousIndex);

  // Insert the control at the new index
  formArray.insert(currentIndex, controlToMove);

  if (this.data?.dialogType === 'manage_dept') {
    const shortNameArray = this.manageDataForm?.get('externalDepartmentShortName') as FormArray;
    if (shortNameArray && shortNameArray.length > previousIndex) {
      const shortNameControlToMove = shortNameArray.at(previousIndex);
      shortNameArray.removeAt(previousIndex);
      shortNameArray.insert(currentIndex, shortNameControlToMove);
    }
  }
}

  onDragStart(index: number) {
    this.isDragging[index] = true;
  }

  onDragEnd(index: number) {
    this.isDragging[index] = false;
    this.enableSave = true
    this.showButtonCont = true
  }

  autoScroll(event: CdkDragMove): void {
    const scrollContainer = document.querySelector('.orgApp-mng-dept-main-div');
    if (!scrollContainer) return;

    const { y } = event.pointerPosition;
    const rect = scrollContainer.getBoundingClientRect();

    const scrollSpeed = 20;

    if (y < rect.top + 50) {
      // scroll up
      scrollContainer.scrollTop -= scrollSpeed;
    } else if (y > rect.bottom - 50) {
      // scroll down
      scrollContainer.scrollTop += scrollSpeed;
    }
  }


  submitForm() {
    let newArray = this.dialogListData.map((item: any, index: any) => ({
      orderOfListing: index + 1,
      memberTypeId: item.id,
    }));
    this.orderingDataEvent.emit(newArray);
    this.loadSpinner();
  }

  loadSpinner() {
    this.editSpinnerRef = this.dialog.open(SpinnerComponent, {
      disableClose: true,
      panelClass: 'transparent-dialog',
    });
  }
  closeDialog(data: any) {
    if (data === 'success') {
      this.editSpinnerRef.close();
    }
  }
  toggleActive(type: string, id: string, newState: boolean): void {
    if (type === 'department') {
      this.editDepartmentState.emit({ id, active: newState });
    } else if (type === 'memberType') {
      this.editMemberTypeState.emit({ id, active: newState });
    }
  }
  isControlDirty(index: number): boolean {      //to check whether the user has made any changes to dept name or externalDepartmentShortName, If not, then disable the update button 
    const nameArray = this.manageDataForm?.get('name') as FormArray;
    const shortNameArray = this.manageDataForm?.get('externalDepartmentShortName') as FormArray;
    const nameVal = (nameArray?.at(index)?.value || '').trim();
    const origNameVal = (this.originalValues[index] || '').trim();
    const shortNameVal = (shortNameArray?.at(index)?.value || '').trim();
    const origShortNameVal = (this.originalExternalDepartmentShortNameValues[index] || '').trim();
    const nameChanged = nameVal !== origNameVal;
    const shortNameChanged = shortNameVal !== origShortNameVal;
    const isNameValid = nameVal.length > 0 && !this.dataEditExistError[index];
    const isShortNameValid = !shortNameArray?.at(index) || shortNameArray.at(index).valid;
    return (nameChanged || shortNameChanged) && isNameValid && isShortNameValid;
  }

  isMemberTypeControlDirty(index: number, item: any): boolean {
    if (!this.dataList || !this.dataList[index]) return false;
    
    // Check if name has changed
    const currentName = (this.dialogDataControls?.at(index)?.value || '').trim();
    const originalName = (this.dataList[index].name || '').trim();
    if (currentName !== originalName) {
      return true;
    }

    // Check if dutyCoveredByMemberTypes has changed
    const originalDuties = this.dataList[index].dutyCoveredByMemberTypes || [];
    const currentDuties = item.dutyCoveredByMemberTypes || [];
    if (originalDuties.length !== currentDuties.length) {
      return true;
    }
    const originalSet = new Set(originalDuties);
    for (const duty of currentDuties) {
      if (!originalSet.has(duty)) {
        return true;
      }
    }

    // Check if isManager has changed
    if (this.isManagerEdit !== this.dataList[index].isManager) {
      return true;
    }

    return false;
  }

   updateMmemberTypeSelection(event:any, memberformGroup:any): void {
      if(memberformGroup.dutyCoveredByMemberTypes) {
        memberformGroup.dutyCoveredByMemberTypes =  event?.value;
      }
  }

  get selectedNames(): string[] {
    const selectedIds = this.manageDataForm?.get('dutyCoveredByMemberTypes')?.value || [];
    if (!selectedIds.length || !this.dialogListData) return [];
    return this.dialogListData
      .filter((item: { id: any; }) => selectedIds.includes(item.id))
      .map((item: { value: any; }) => item.value);
  }
  getFilteredMemberTypes(currentId: string) {
    return this.dialogListData.filter((item: { id: string; }) => item.id !== currentId);
  }

  getMemberTypeName(selectedIds: string[]): string[] {
    
    if (!Array.isArray(selectedIds) || !selectedIds.length) return [];
    
    const result = this.dialogListData
      .filter((item: { id: string }) => selectedIds.includes(item.id))
      .map((item: { value: string }) => item.value);

    return result;
  }

  toggleAvailableForPlanning(id: string, newState: boolean, item: any): void {
    if (item) {
      console.log("item herer", item);
      
      item.availableForPlanning = newState;   // keep local model in sync
      this.editDepartmentPlanningState.emit({
        id,
        linkageStatus: item['linkageStatus'],
        active: item['active'],
        departmentName: item['value'],
        externalDepartmentShortName: item['externalDepartmentShortName'],
        availableForPlanning: newState
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}